import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import {
  IAllocationSector,
  useDeleteAllocationSectorMutation,
  useAllocationSectorCreatedSubscription,
  useAllocationSectorUpdatedSubscription,
  useAllocationSectorDeletedSubscription,
  useGetAllocationSectorsQuery
} from '../../../../generated/graphql';
import { canUserCreate } from '../../../../utils/rightUtils';
import { Box } from '@mui/material';
import DenseTable from '../../../Common/Tables/DenseTable';
import AllocationSectorForm from './AllocationSectorForm';
import InnerLoader from '../../../Common/InnerLoader';

interface IProps {
  stockId: string;
  companyName: string;
}

const AllocationSectorTab: React.FunctionComponent<IProps> = ({ stockId, companyName }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [allData, setAllData] = useState<IAllocationSector[]>([]);
  const [allocationSector, setAllocationSector] = useState<IAllocationSector | null>(null);
  const [openAllSectorForm, setOpenAllSectorForm] = useState(false);
  const [deleteAllocationSector] = useDeleteAllocationSectorMutation();
  const canCreate = canUserCreate(state.user, Types.Stock);
  const createAllocationSectorSubscription = useAllocationSectorCreatedSubscription({
    variables: { stock_id: stockId }
  });
  const updateAllocationSectorSubscription = useAllocationSectorUpdatedSubscription({
    variables: { stock_id: stockId }
  });
  const deleteAllocationSecotrSubscription = useAllocationSectorDeletedSubscription({
    variables: { stock_id: stockId }
  });
  const { data, loading } = useGetAllocationSectorsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      stock_id: parseInt(stockId)
    }
  });

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as IAllocationSector[]);
    }
  }, [data]);

  useEffect(() => {
    if (createAllocationSectorSubscription.data?.result) {
      addAllSector(createAllocationSectorSubscription.data?.result);
    }
    if (deleteAllocationSecotrSubscription.data?.result) {
      removeAllocationSector(deleteAllocationSecotrSubscription.data.result);
    }
    if (updateAllocationSectorSubscription.data?.result) {
      updateAllSector(updateAllocationSectorSubscription.data?.result);
    }
  }, [
    createAllocationSectorSubscription.data,
    updateAllocationSectorSubscription.data,
    deleteAllocationSecotrSubscription.data
  ]);

  const columns = [
    { title: 'Details', sorting: false, width: 82 },
    {
      title: 'Date Year',
      field: 'year',
      width: 100
    },
    {
      title: 'Company Name',
      width: 900,
      render: () => companyName,
      sorting: false
    },
    {
      title: 'ID Sector type',
      field: 'sector_id',
      width: 273
    },
    {
      title: 'Sector %',
      field: 'value',
      width: 180
    },
    { title: 'Remove', sorting: false }
  ];

  const handleDetails = (rowData: IAllocationSector) => {
    setAllocationSector(rowData);
    setOpenAllSectorForm(true);
  };

  const removeAllocationSector = (input: IAllocationSector) => {
    const result = (allData as IAllocationSector[]).filter((l) => l.id !== input.id);
    setAllData(result);
  };

  const handleRemove = (rowData: IAllocationSector) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete sector allocation from ${rowData.year.toString()}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (allocationSector: IAllocationSector) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteAllocationSector({
      variables: {
        id: allocationSector.id
      },
      update() {
        removeAllocationSector(allocationSector);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: `Sector allocation from ${allocationSector.year.toString()} deleted`
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const handleClose = (saved: boolean, isNew?: boolean, allSectorInput?: IAllocationSector) => {
    if (saved && allSectorInput) {
      if (isNew) {
        addAllSector(allSectorInput);
      } else {
        updateAllSector(allSectorInput);
      }
    }

    setAllocationSector(null);
    setOpenAllSectorForm(false);
  };

  const addAllSector = (allSectorInput: IAllocationSector) =>
    setAllData([allSectorInput, ...allData]);

  const updateAllSector = (allSectorInput: IAllocationSector) =>
    setAllData(allData.map((k) => (k.id === allSectorInput.id ? allSectorInput : k)));

  return (
    <Box>
      <Box className="print\:w-full">
        {loading ? (
          <InnerLoader />
        ) : (
          <Box style={{ marginTop: '10px' }}>
            <DenseTable
              columns={columns}
              data={allData}
              loading={loading}
              handleDetails={handleDetails}
              handleRemove={handleRemove}
              createButtonProps={{
                id: 'create-allocation-sector-button',
                onClick: () => setOpenAllSectorForm(true),
                text: 'Create',
                disabled: !canCreate
              }}
              maxHeight={800}
              pageSize={15}
            />
          </Box>
        )}
        {openAllSectorForm && (
          <AllocationSectorForm
            stockId={stockId}
            companyName={companyName}
            handleClose={handleClose}
            allSectorInput={allocationSector}
          />
        )}
      </Box>
    </Box>
  );
};

export default AllocationSectorTab;
